import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { Acl, DetailTableAce } from '@/accessControl/itemAclModal.utilities';
import { Table } from '@/core/Table.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { Checkbox } from '@/core/Checkbox.atom';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { sqWorkbooksApi } from '@/sdk/api/WorkbooksApi';
import { errorToast } from '@/utilities/toast.utilities';

interface AclModalAdvancedTabProps {
  loadingDetails: boolean;
  detailTableEntries: DetailTableAce[];
  togglePermissionsInheritance: () => void;
  itemAcl: Acl;
  hasManagePermission: boolean;
  /** Defaults to true **/
  allowDisablingPermissionInheritance?: boolean;
  workbookId?: string;
  itemId?: string;
  item?: any;
}

export const AclModalAdvancedTab: React.FunctionComponent<AclModalAdvancedTabProps> = (props) => {
  const {
    loadingDetails,
    detailTableEntries,
    togglePermissionsInheritance,
    itemAcl,
    hasManagePermission,
    allowDisablingPermissionInheritance = true,
    workbookId,
    itemId,
    item,
  } = props;
  const { t } = useTranslation();

  const [workbookData, setWorkbookData] = useState<any>(null);
  const [loadingWorkbookData, setLoadingWorkbookData] = useState(false);

  // Determine if this is a workbook
  let isCurrentItemWorkbookType = _.isEqual(workbookId, itemId);
  if (!_.isNil(item)) {
    isCurrentItemWorkbookType = [SeeqNames.Types.Analysis, SeeqNames.Types.Topic].includes(item.type);
  }

  useEffect(() => {
    if (isCurrentItemWorkbookType && itemId && !workbookData && !loadingWorkbookData) {
      setLoadingWorkbookData(true);
      (async () => {
        try {
          const response: any = await (sqWorkbooksApi.getWorkbook({ id: itemId }) as any);
          const data = response?.data ?? response;
          if (!_.isNil(data)) {
            setWorkbookData(data);
          }
        } catch (error) {
          errorToast({ httpResponseOrError: error });
        } finally {
          setLoadingWorkbookData(false);
        }
      })();
    }
  }, [isCurrentItemWorkbookType, itemId, workbookData, loadingWorkbookData]);

  // Get the parent folder name for workbooks
  const getParentFolderName = () => {
    if (!isCurrentItemWorkbookType) {
      return null;
    }

    if (workbookData?.ancestors?.length > 0) {
      return workbookData.ancestors[workbookData.ancestors.length - 1]?.name;
    }

    if (item?.ancestors?.length > 0) {
      return item.ancestors[item.ancestors.length - 1]?.name;
    }

    return null;
  };

  const parentFolderName = getParentFolderName();

  // Determine checkbox state (inverted from backend value)
  const isInheritanceEnabled = !itemAcl.permissionsInheritanceDisabled;

  const getInheritanceLabel = () => {
    if (!isCurrentItemWorkbookType) {
      return 'ITEM_ACL.ADVANCED.INHERIT_FROM_PARENT_LABEL';
    }
    return parentFolderName
      ? 'ITEM_ACL.ADVANCED.INHERIT_FROM_PARENT_FOLDER_LABEL'
      : 'ITEM_ACL.ADVANCED.INHERIT_FROM_PARENT_FOLDER_NONE_LABEL';
  };

  const getInheritanceDescription = () => {
    if (!isCurrentItemWorkbookType) {
      return 'ITEM_ACL.ADVANCED.PERMISSION_INHERITANCE_DISABLED_DESCRIPTION';
    }
    return parentFolderName
      ? 'ITEM_ACL.ADVANCED.INHERIT_FROM_PARENT_FOLDER_DESCRIPTION'
      : 'ITEM_ACL.ADVANCED.INHERIT_FROM_PARENT_FOLDER_NONE_DESCRIPTION';
  };

  const getDisabledIndicator = () => {
    if (!isCurrentItemWorkbookType) {
      return 'ITEM_ACL.ADVANCED.PERMISSION_INHERITANCE_DISABLED_INDICATOR';
    }
    return 'ITEM_ACL.ADVANCED.INHERITANCE_FROM_PARENT_FOLDER_DISABLED';
  };

  const columns = [
    {
      accessor: 'name',
      sortable: false,
      filterable: false,
      cellStyle: { width: 100, padding: '0 7px' },
      header: 'ACCESS_CONTROL.NAME',
    },
    {
      accessor: 'permissions',
      sortable: false,
      filterable: false,
      cellStyle: { width: 100, padding: '0 7px' },
      header: 'ACCESS_CONTROL.PERMISSIONS',
    },
    {
      accessor: 'inheritedFrom',
      sortable: false,
      filterable: false,
      cellStyle: { width: 100, padding: '0 7px' },
      header: 'ACCESS_CONTROL.INHERITED_FROM',
    },
  ];

  return (
    <div className="flexRowContainer flexFill mt15" id="aclGrid">
      {loadingDetails && (
        <div className="flexRowContainer flexCenter mt30 mb15">
          <span className="mb5">{t('ACCESS_CONTROL.LOADING_DETAILS')}</span>
          <IconWithSpinner spinning={true} />
        </div>
      )}
      {t('ITEM_ACL.LABEL')}
      {itemAcl.permissionsInheritanceDisabled && (
        <span className="sq-text-warning cursorPointer link-no-underline">{t(getDisabledIndicator())}</span>
      )}
      <div className="flexFill aclGrid">
        <Table columns={columns} items={detailTableEntries} />
      </div>
      {hasManagePermission && allowDisablingPermissionInheritance && (
        <div
          className="animateHeight250Linear lightGreyBorder roundedBorder flexRowContainer flexJustifyCenter
      flexFill height-100 mb10">
          <div
            className="checkbox m10 cursorPointer flexRowContainer"
            onClick={(e) => {
              e.preventDefault();
              togglePermissionsInheritance();
            }}
            id="specDisableInheritanceCheckbox">
            <div className="flexColumnContainer">
              <Checkbox
                id="togglePermissionsCheckbox"
                skipMemo={true}
                isChecked={isInheritanceEnabled}
                label={t(getInheritanceLabel(), parentFolderName ? { folderName: parentFolderName } : {})}
              />
            </div>
            <div className="ml21 mr10">
              {t(getInheritanceDescription(), parentFolderName ? { folderName: parentFolderName } : {})}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
